import { useCallback, useMemo } from "react";

import { compareFractionalOrder } from "@/lib/compare-fractional-order";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { getStatusListForWorkspaceQueryDocument } from "./status.gql";

interface UseGetStatusListProps {
  workspaceId?: string | undefined;
  enabled?: boolean;
}

export const useGetStatusListForWorkspace = ({ workspaceId, enabled = true }: UseGetStatusListProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.statuses.list(workspaceId ?? ""),
    queryFn: async () => {
      if (!workspaceId) {
        throw new Error("Workspace ID is required");
      }

      const client = createOperationClient("GetStatusListForWorkspace");
      const data = await client.request(getStatusListForWorkspaceQueryDocument, {
        input: { workspaceId },
      });

      return data.statusListForWorkspace;
    },
    enabled: enabled && !!workspaceId,
    staleTime: 2 * 60 * 1000, // 2 minutes for list data
  });

  const statusList = useMemo(() => {
    const unsortedStatusList = queryResult.data ?? [];
    return [...unsortedStatusList].sort((a, b) => compareFractionalOrder(a.order, b.order));
  }, [queryResult.data]);

  const findStatusById = useCallback(
    (id: string) => {
      return statusList.find((s) => s.id === id);
    },
    [statusList],
  );

  return {
    statusList,
    findStatusById,
    loading: queryResult.isLoading,
    error: queryResult.error,
  };
};
